import { Button } from '@ctw/shared/components/button';
import { PatientSummarizationBanner } from '@ctw/shared/content/summarization/patient-summarization-banner';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useUiFlag } from '@ctw/shared/hooks/use-ui-preference';
import { formatDateToLocalHumanDateString, numberOfDaysBetween } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import {
  faArrowTrendUp,
  faCircleExclamation,
  faRotateRight,
  faSparkles,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays, endOfMonth } from 'date-fns';
import pluralize from 'pluralize';
import { useCallback, useMemo } from 'react';
import {
  getUiFlagLimitApproachingBannerHidden,
  getUiFlagLimitReachedBannerHidden,
  usePatientSummarizationUpgradeModal,
} from './patient-summarization-status-card';

export interface SummarizationLimitBannerProps {
  variant?: 'inline' | 'standalone';
  dismissable?: boolean;
  summaryJobId?: string;
  summaryJobStatus?: string;
  upsell?: boolean;
}

export const PatientSummarizationLimitApproachingBanner = ({
  variant = 'inline',
  dismissable = true,
  summaryJobId,
  summaryJobStatus,
  upsell = true,
}: SummarizationLimitBannerProps) => {
  const { summariesAllotted, summariesUsed, showMetering } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const flagKey = useMemo(() => getUiFlagLimitApproachingBannerHidden(), []);
  const [flagHidden, setFlagHidden] = useUiFlag(flagKey, false);
  const hidden = dismissable && flagHidden;

  const hideBanner = useCallback(() => {
    setFlagHidden(true);
  }, [setFlagHidden]);

  const numberOfDaysUntilReset = numberOfDaysBetween({
    start: new Date(),
    end: addDays(endOfMonth(new Date()), 1),
  });

  if (!showMetering) {
    return null;
  }

  const body = (
    <>
      {summariesUsed.toLocaleString('en-US')} of {summariesAllotted.toLocaleString('en-US')} free
      summaries have been used this month. Resets in{' '}
      {pluralize('day', numberOfDaysUntilReset, true)}.
    </>
  );

  const action = !upsell ? undefined : variant === 'standalone' ? (
    <Button
      type="button"
      variant="primary"
      onClick={openUpgradeModal}
      className={tw`bg-caution-main text-white hover:bg-caution-main/90`}
      telemetryInteractionName="patient_summary_upgrade_modal_opened"
      telemetryObfuscatedIdentifier="Learn More"
      telemetryContext={{
        summaryJobId,
        summaryJobStatus,
        upgradeModalTrigger: 'learn-more-link',
      }}
    >
      <div className={tw`flex items-center space-x-2`}>
        <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
        <div className={tw`whitespace-nowrap text-sm`}>Learn More</div>
      </div>
    </Button>
  ) : (
    <Button
      type="button"
      variant="link"
      onClick={openUpgradeModal}
      className={tw`whitespace-nowrap font-medium text-caution-text underline`}
      telemetryInteractionName="patient_summary_upgrade_modal_opened"
      telemetryObfuscatedIdentifier="Learn More"
      telemetryContext={{
        summaryJobId,
        summaryJobStatus,
        upgradeModalTrigger: 'learn-more-link',
      }}
    >
      Learn More
    </Button>
  );

  return (
    <PatientSummarizationBanner
      tone="caution"
      icon={faCircleExclamation}
      title="You're approaching your monthly AI summary limit"
      body={body}
      action={action}
      dismissable={dismissable}
      isDismissed={hidden}
      onDismiss={hideBanner}
      variant={variant}
    />
  );
};

export const PatientSummarizationLimitReachedBanner = ({
  variant = 'inline',
  dismissable = true,
  summaryJobId,
  summaryJobStatus,
  upsell = true,
}: SummarizationLimitBannerProps) => {
  const { summariesAllotted, showMetering } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const flagKey = useMemo(() => getUiFlagLimitReachedBannerHidden(), []);
  const [flagHidden, setFlagHidden] = useUiFlag(flagKey, false);
  const hidden = dismissable && flagHidden;

  const hideBanner = useCallback(() => {
    setFlagHidden(true);
  }, [setFlagHidden]);

  const formattedSummaryLimitResetDate = formatDateToLocalHumanDateString(
    addDays(endOfMonth(new Date()), 1),
  );

  if (!showMetering) {
    return null;
  }

  const body = (
    <>
      You've used all {summariesAllotted.toLocaleString('en-US')} free summaries this month.{' '}
      {upsell ? (
        <>
          Upgrade for unlimited access or wait until your quota resets (
          {formattedSummaryLimitResetDate}).
        </>
      ) : (
        <>Your quota will reset on {formattedSummaryLimitResetDate}.</>
      )}
    </>
  );

  const action =
    upsell && variant === 'standalone' ? (
      <Button
        type="button"
        variant="primary-destructive"
        onClick={openUpgradeModal}
        telemetryInteractionName="patient_summary_upgrade_modal_opened"
        telemetryObfuscatedIdentifier="Upgrade Now"
        telemetryContext={{
          summaryJobId,
          summaryJobStatus,
          upgradeModalTrigger: 'upgrade-now-button',
        }}
      >
        <div className={tw`flex items-center space-x-2`}>
          <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
          <div className={tw`whitespace-nowrap text-sm`}>Upgrade Now</div>
        </div>
      </Button>
    ) : undefined;

  return (
    <PatientSummarizationBanner
      tone="error"
      icon={faCircleExclamation}
      title={variant === 'standalone' ? "You've reached your monthly AI summary limit" : undefined}
      body={body}
      action={action}
      dismissable={dismissable}
      isDismissed={hidden}
      onDismiss={hideBanner}
      variant={variant}
    />
  );
};

export const PatientSummarizationInsufficientDataBanner = () => {
  return (
    <PatientSummarizationBanner
      tone="caution"
      icon={faSparkles}
      title="Patient data hasn't finished processing"
      body="The patient history job may still be running — check back shortly."
      variant="standalone"
      background="white"
    />
  );
};

export interface PatientSummarizationGenerationErrorBannerProps {
  onTryAgain: () => void;
  summaryJobId?: string;
}

export const PatientSummarizationGenerationErrorBanner = ({
  onTryAgain,
  summaryJobId,
}: PatientSummarizationGenerationErrorBannerProps) => {
  return (
    <PatientSummarizationBanner
      tone="error"
      icon={faTriangleExclamation}
      title="Something went wrong"
      body="The summary couldn't be generated. Please try again."
      variant="standalone"
      background="white"
      action={
        <Button
          type="button"
          variant="secondary-destructive"
          onClick={onTryAgain}
          telemetryInteractionName="patient_summary_try_again_clicked"
          telemetryObfuscatedIdentifier="Try Again"
          telemetryContext={{ summaryJobId }}
        >
          <div className={tw`flex items-center space-x-2`}>
            <FontAwesomeIcon icon={faRotateRight} className={tw`h-4 w-4`} />
            <div className={tw`whitespace-nowrap text-sm`}>Try again</div>
          </div>
        </Button>
      }
    />
  );
};
